import React, {useState} from 'react';
import {Button, DatePicker, Form, Input, InputNumber, Modal, Select, Table} from 'antd';
import dayjs from 'dayjs';
import {internalApi} from "../../../common/axios";
import {DIALOG_KEY} from "../../../shared/utils/enum";
import {buildUrl} from "../../../shared/utils/net";
import {CancelButton, DeleteButton, FixButton, SaveButton} from "../../../shared/components/buttons/CustomButtons";
import {useMissionPresets} from "./useTrophyChallenge";
import {useQueryClient} from "@tanstack/react-query";
import toast from "react-hot-toast";

const {Option} = Select;

const TrophyChallengeMissionGroupEditModal = ({record, handleClose, dialogRef, handleSetDialog, modalOptions}) => {
    const queryClient = useQueryClient();
    const {data: presetGIdxList} = useMissionPresets();

    const allRows = modalOptions?.allRows || [];
    const initialGroupIdx = record?.groupIdx || (() => {
        if (!allRows.length) return 1;
        return Math.max(...allRows.map(r => r.groupIdx)) + 1;
    })();

    const [groupIdx, setGroupIdx] = useState(initialGroupIdx);
    const [memo, setMemo] = useState(record?.memo || '');
    const [items, setItems] = useState(record?.items || []);
    const [selectedGIdx, setSelectedGIdx] = useState(record?.tableIdx || 0);

    const handleApplyPreset = () => {
        if (selectedGIdx == null) return;
        internalApi.get(`trophy_challenge/mission_presets/${selectedGIdx}`).then(res => {
            const missions = res.data.missions || [];
            setGroupIdx(selectedGIdx);
            setItems(missions);
        }).catch(() => {
            toast.error('프리셋 로드 실패');
        });
    };

    const handleUpdateItem = (index, key, value) => {
        setItems(prev => prev.map((item, i) => i === index ? {...item, [key]: value} : item));
    };

    const doSave = () => {
        return internalApi.post('trophy_challenge/mission_group', {
            data: {groupIdx, memo, missions: items, tableIdx: selectedGIdx},
        }).then(res => {
            toast.success(res.data.message);
            queryClient.invalidateQueries({queryKey: ['trophyChallengeMissionGroup']});
            handleSetDialog([{key: DIALOG_KEY.OPEN, value: false}, {key: DIALOG_KEY.CONFIRM_TEXT, value: ''}]);
            handleClose(true);
        }).catch(err => {
            toast.error(err?.response?.data?.message || '저장 실패');
            console.error(err);
        });
    };

    const handleSave = (e) => {
        e.preventDefault();
        if (!groupIdx || groupIdx <= 0) {
            toast.error('그룹 번호는 0보다 커야 합니다.');
            return;
        }
        if (items.length === 0) {
            toast.error('미션을 하나 이상 추가해주세요.');
            return;
        }

        const isDuplicate = groupIdx !== record?.groupIdx && allRows.some(r => r.groupIdx === groupIdx);
        if (isDuplicate) {
            Modal.confirm({
                title: '중복 확인',
                content: '이미 존재하는 번호입니다. 내용을 수정해서 저장하시겠습니까?',
                okText: '예',
                cancelText: '아니오',
                onOk: doSave,
            });
        } else {
            return doSave();
        }
    };

    const handleDelete = (e) => {
        e.preventDefault();
        const currentDialog = dialogRef.current;
        if (currentDialog.confirmText === 'DELETE') {
            return internalApi.delete(buildUrl(`trophy_challenge/mission_group/${groupIdx}`)).then(res => {
                toast.success(res.data.message);
                queryClient.invalidateQueries({queryKey: ['trophyChallengeMissionGroup']});
                handleSetDialog([{key: DIALOG_KEY.OPEN, value: false}, {key: DIALOG_KEY.CONFIRM_TEXT, value: ''}]);
                handleClose(true);
            }).catch(err => {
                toast.error('삭제 실패');
                console.error(err);
            });
        } else {
            toast.error("잘못된 확인 문자입니다. DELETE 를 입력하세요.");
            handleSetDialog([{key: DIALOG_KEY.CONFIRM_TEXT, value: ''}]);
        }
    };

    const handleFix = () => {
        internalApi.post('trophy_challenge/mission_group/fix', {groupIdx}).then(res => {
            toast.success(res.data.message);
            queryClient.invalidateQueries({queryKey: ['trophyChallengeMissionGroup']});
            handleSetDialog([{key: DIALOG_KEY.OPEN, value: false}, {key: DIALOG_KEY.CONFIRM_TEXT, value: ''}]);
            handleClose(true);
        }).catch(() => {
            toast.error('고정 실패');
        });
    };

    const tableCols = [
        {title: '순서', key: 'groupSeq', width: 60, render: (_, __, i) => i + 1},
        {title: '미션명', dataIndex: 'name', key: 'name', width: 130},
        {title: '설명', dataIndex: 'subName', key: 'subName', width: 130},
        {title: 'conditionIdx', dataIndex: 'conditionIdx', key: 'conditionIdx', width: 110},
        {title: 'conditionType', dataIndex: 'conditionType', key: 'conditionType', width: 130},
        {
            title: 'conditionValue',
            key: 'conditionValue',
            width: 120,
            render: (_, item, index) => (
                <InputNumber
                    size="small"
                    value={item.conditionValue}
                    style={{width: '100%'}}
                    onChange={v => handleUpdateItem(index, 'conditionValue', v ?? 0)}
                />
            ),
        },
        {
            title: 'conditionCount',
            key: 'conditionCount',
            width: 120,
            render: (_, item, index) => (
                <InputNumber
                    size="small"
                    value={item.conditionCount}
                    style={{width: '100%'}}
                    onChange={v => handleUpdateItem(index, 'conditionCount', v ?? 0)}
                />
            ),
        },
        {
            title: 'rewardIdx1',
            key: 'rewardIdx1',
            width: 100,
            render: (_, item, index) => (
                <InputNumber
                    size="small"
                    min={0}
                    value={item.rewardIdx1}
                    style={{width: '100%'}}
                    onChange={v => handleUpdateItem(index, 'rewardIdx1', v ?? 0)}
                />
            ),
        },
        {
            title: 'rewardIdx2',
            key: 'rewardIdx2',
            width: 100,
            render: (_, item, index) => (
                <InputNumber
                    size="small"
                    min={0}
                    value={item.rewardIdx2}
                    style={{width: '100%'}}
                    onChange={v => handleUpdateItem(index, 'rewardIdx2', v ?? 0)}
                />
            ),
        },
        {
            title: 'rewardIdx3',
            key: 'rewardIdx3',
            width: 100,
            render: (_, item, index) => (
                <InputNumber
                    size="small"
                    min={0}
                    value={item.rewardIdx3}
                    style={{width: '100%'}}
                    onChange={v => handleUpdateItem(index, 'rewardIdx3', v ?? 0)}
                />
            ),
        },
        {
            title: 'startDate',
            key: 'startDate',
            width: 175,
            render: (_, item, index) => (
                <DatePicker
                    size="small"
                    showTime={{format: 'HH:mm'}}
                    format="YYYY-MM-DD HH:mm"
                    value={item.startDate ? dayjs.unix(item.startDate) : null}
                    onChange={date => handleUpdateItem(index, 'startDate', date ? date.unix() : 0)}
                    style={{width: '100%'}}
                />
            ),
        },
        {
            title: 'endDate',
            key: 'endDate',
            width: 175,
            render: (_, item, index) => (
                <DatePicker
                    size="small"
                    showTime={{format: 'HH:mm'}}
                    format="YYYY-MM-DD HH:mm"
                    value={item.endDate ? dayjs.unix(item.endDate) : null}
                    onChange={date => handleUpdateItem(index, 'endDate', date ? date.unix() : 0)}
                    style={{width: '100%'}}
                />
            ),
        }
    ];

    return (
        <div style={{marginTop: 26}}>
            <Form layout="vertical">
                <div style={{display: 'flex', gap: 8, alignItems: 'flex-end', marginBottom: 12}}>
                    <Form.Item label="데이터 프리셋 로드" style={{flex: 1, marginBottom: 0}}>
                        <Select
                            value={selectedGIdx}
                            onChange={setSelectedGIdx}
                            placeholder="CSV gIdx 선택"
                            showSearch
                        >
                            {(presetGIdxList || []).map(g => (
                                <Option key={g} value={g}>gIdx {g}</Option>
                            ))}
                        </Select>
                    </Form.Item>
                    <Form.Item style={{marginBottom: 0}}>
                        <Button onClick={handleApplyPreset}>적용</Button>
                    </Form.Item>
                </div>

                <div style={{display: 'flex', gap: 12, alignItems: 'flex-end'}}>
                    <Form.Item label="그룹 번호" style={{width: 140, marginBottom: 12}}>
                        <InputNumber
                            min={1}
                            value={groupIdx}
                            onChange={v => setGroupIdx(v || 0)}
                            style={{width: '100%'}}
                        />
                    </Form.Item>
                    <Form.Item label="메모 (전체 항목 공통)" style={{flex: 1, marginBottom: 12}}>
                        <Input
                            value={memo}
                            onChange={e => setMemo(e.target.value)}
                            placeholder="관리용 메모"
                        />
                    </Form.Item>
                </div>

                <div style={{overflowX: 'auto', marginBottom: 12}}>
                    <Table
                        columns={tableCols}
                        dataSource={items.map((it, i) => ({...it, key: i}))}
                        pagination={false}
                        size="small"
                        scroll={{x: 'max-content'}}
                        locale={{emptyText: '프리셋을 로드하거나 기존 그룹을 선택하세요.'}}
                    />
                </div>

                <div style={{position: 'sticky', bottom: 0, paddingTop: 8, background: 'var(--ant-color-bg-elevated, #fff)', zIndex: 10}}>
                    {record?.groupIdx > 0 && (
                        <FixButton
                            onClick={handleFix}
                            handleSetDialog={handleSetDialog}
                            disabled={record?.isFixed}
                        />
                    )}
                    <SaveButton
                        onClick={handleSave}
                        handleSetDialog={handleSetDialog}
                        disabled={record?.isFixed}
                    />
                    <CancelButton onClick={handleClose}/>
                    <DeleteButton
                        onClick={handleDelete}
                        handleSetDialog={handleSetDialog}
                        display={record?.groupIdx > 0 ? 'flex' : 'none'}
                        disabled={record?.isFixed}
                    />
                </div>
            </Form>
        </div>
    );
};

export default TrophyChallengeMissionGroupEditModal;
